import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Link } from "wouter";
import { ArrowLeft, Star, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const LS_KEY = "rheumhub_doctor_interview_notes";

interface Domain {
  key: string;
  title: string;
  desc: string;
  questions: string[];
}

const DOMAINS: Domain[] = [
  {
    key: "inspiring_shared_purpose",
    title: "1. Inspiring Shared Purpose",
    desc: "Demonstrates a commitment to and passion for the values of the NHS and rheumatology. Understands and can articulate the broader mission of the NHS and how rheumatology contributes to population health.",
    questions: [
      "Tell me about a time you inspired your team around a shared goal or vision for a service.",
      "How have you demonstrated commitment to NHS values in your clinical or leadership practice?",
      "Describe a situation where you helped colleagues understand how their work connects to the broader NHS mission.",
      "What is your vision for rheumatology services in the NHS over the next 10 years, and how would you contribute to it?",
    ],
  },
  {
    key: "leading_with_care",
    title: "2. Leading with Care",
    desc: "Demonstrates compassionate leadership, understanding the importance of emotional intelligence, psychological safety, and staff wellbeing in high-performing teams.",
    questions: [
      "Tell me about a time you supported a colleague who was struggling, either personally or professionally.",
      "How do you create psychological safety in your clinical team?",
      "Describe a situation where you had to balance patient care priorities with the wellbeing of your team.",
      "How have you promoted staff wellbeing or addressed burnout in your department?",
    ],
  },
  {
    key: "evaluating_information",
    title: "3. Evaluating Information",
    desc: "Uses evidence, data, and sound judgement to make well-reasoned decisions. Critically appraises information and can explain the basis of clinical and managerial decisions clearly.",
    questions: [
      "Tell me about a time you used data or evidence to change clinical practice in your department.",
      "Describe a complex clinical situation where you had to weigh up conflicting evidence and make a decision.",
      "How do you approach critical appraisal of a new guideline or piece of research?",
      "Give an example of when you identified a problem in your service using data and led an improvement.",
    ],
  },
  {
    key: "connecting_our_service",
    title: "4. Connecting Our Service",
    desc: "Works effectively across boundaries — with GPs, other specialties, allied health professionals, commissioners, and patients — to deliver integrated, patient-centred care.",
    questions: [
      "Tell me about a time you worked with professionals outside rheumatology to improve patient care.",
      "How have you developed or improved relationships with primary care colleagues?",
      "Describe a situation where you had to negotiate or collaborate across organisational boundaries.",
      "Give an example of how you have involved patients or carers in service design or improvement.",
    ],
  },
  {
    key: "sharing_the_vision",
    title: "5. Sharing the Vision",
    desc: "Communicates strategy, change, and new ideas in a compelling and accessible way. Brings colleagues along on a journey of improvement.",
    questions: [
      "Tell me about a change you led in your department and how you communicated it to your team.",
      "How have you presented a complex clinical or strategic idea to a non-specialist audience?",
      "Describe a time when you had to convince sceptical colleagues to adopt a new approach.",
      "How would you communicate the case for a new rheumatology service development to a Trust board?",
    ],
  },
  {
    key: "engaging_the_team",
    title: "6. Engaging the Team",
    desc: "Builds high-performing, inclusive teams. Motivates and involves others, values diversity, and creates conditions for collaboration and innovation.",
    questions: [
      "Tell me about a time you successfully engaged a team that had low morale or was resistant to change.",
      "How have you promoted diversity and inclusion in your clinical or research team?",
      "Describe your approach to involving junior members of your team in decision-making and service development.",
      "Give an example of building or strengthening a multidisciplinary team in rheumatology.",
    ],
  },
  {
    key: "holding_to_account",
    title: "7. Holding to Account",
    desc: "Sets clear expectations, gives constructive feedback, and takes appropriate action when standards are not met. Ensures quality and safety through transparent accountability.",
    questions: [
      "Tell me about a time you had to address underperformance in a colleague or team member.",
      "How do you ensure quality standards and patient safety are maintained in your clinical area?",
      "Describe a situation where you had to raise a concern about practice or behaviour in your department.",
      "Give an example of when you gave difficult feedback and how you managed the conversation.",
    ],
  },
  {
    key: "developing_capability",
    title: "8. Developing Capability",
    desc: "Invests in the development of self and others. Mentors, teaches, and coaches the next generation of clinicians. Creates learning cultures and supports continuous development.",
    questions: [
      "Tell me about your most significant contribution to teaching or training in rheumatology.",
      "How have you supported the career development of a junior colleague or trainee?",
      "Describe a learning culture you have helped create in your department.",
      "Give an example of your own professional development and how it has improved your practice.",
    ],
  },
];

function loadNotes(): Record<string, string> {
  try {
    const stored = localStorage.getItem(LS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
}

function saveNotes(val: Record<string, string>) {
  localStorage.setItem(LS_KEY, JSON.stringify(val));
}

export default function InterviewPrep() {
  const [notes, setNotes] = useState<Record<string, string>>(loadNotes);
  const [expandedDomain, setExpandedDomain] = useState<string | null>(DOMAINS[0].key);
  const [resetConfirm, setResetConfirm] = useState(false);

  const handleNoteChange = (key: string, value: string) => {
    const next = { ...notes, [key]: value };
    setNotes(next);
    saveNotes(next);
  };

  const handleReset = () => {
    setNotes({});
    localStorage.removeItem(LS_KEY);
    setResetConfirm(false);
  };

  const notedCount = Object.values(notes).filter((n) => n.trim().length > 0).length;

  return (
    <DoctorLayout title="Consultant Interview Prep">
      <div className="space-y-6">

        <div className="flex items-center gap-3">
          <Link href="/doctor/career">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Career Pathways
            </Button>
          </Link>
        </div>

        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Star className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">Consultant Interview Preparation</h2>
              <p className="text-emerald-300 text-sm">8 NHS Leadership Domains · Mock Questions · Personal Notes</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed mb-3">
            NHS consultant interviews use a structured competency framework based on the NHS Leadership Academy's 8 leadership domains. For each domain, prepare 2-3 STAR examples (Situation, Task, Action, Result) from your clinical and leadership career.
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm text-emerald-300">{notedCount}/{DOMAINS.length} domains with notes</span>
            {resetConfirm ? (
              <div className="flex gap-2">
                <button onClick={handleReset} className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Confirm Reset
                </button>
                <button onClick={() => setResetConfirm(false)} className="text-xs px-3 py-1.5 bg-white/10 text-white rounded-lg hover:bg-white/20">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setResetConfirm(true)}
                className="flex items-center gap-1.5 text-xs text-emerald-300 hover:text-white transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                Reset all notes
              </button>
            )}
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 text-xs text-slate-500">
          Your notes are saved automatically to your browser. They are not uploaded to any server.
        </div>

        <div className="space-y-3">
          {DOMAINS.map((domain) => {
            const expanded = expandedDomain === domain.key;
            const hasNotes = (notes[domain.key] ?? "").trim().length > 0;

            return (
              <div key={domain.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => setExpandedDomain(expanded ? null : domain.key)}
                  className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-900">{domain.title}</h3>
                      {hasNotes && (
                        <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">Notes saved</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{domain.desc}</p>
                  </div>
                  <span className="text-slate-400 shrink-0">{expanded ? "▲" : "▼"}</span>
                </button>

                {expanded && (
                  <div className="px-5 pb-5 space-y-4 border-t border-slate-100 pt-4">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Sample Questions</h4>
                      <ul className="space-y-2">
                        {domain.questions.map((q, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="text-emerald-500 font-bold shrink-0">Q{i + 1}.</span>
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <label htmlFor={`interview-notes-${domain.key}`} className="block text-xs font-medium text-slate-600 mb-1.5">
                        Your Notes <span className="text-slate-400 font-normal">(STAR examples, key points, relevant experience)</span>
                      </label>
                      <textarea
                        id={`interview-notes-${domain.key}`}
                        value={notes[domain.key] ?? ""}
                        onChange={(e) => handleNoteChange(domain.key, e.target.value)}
                        rows={5}
                        placeholder={`Note your STAR examples for "${domain.title}"...\n\nSituation: ...\nTask: ...\nAction: ...\nResult: ...`}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>Tip:</strong> For each domain, prepare two strong STAR examples — one clinical, one leadership or management. Practise answering out loud: timing, clarity, and the ability to be interrupted and redirected are all tested in real interviews. Consider asking a consultant colleague or mentor to run a mock interview before your appointment.
        </div>
      </div>
    </DoctorLayout>
  );
}
